import { Button, Card, message, Select, Statistic, Tooltip } from "antd";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import dayjs from "dayjs";
import { useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { DateRangePicker as ZhDateRangePicker } from "zhihao-ui-react";

import { dashboardService } from "@/api/services/dashboardService";
import { useUserStore } from "@/stores/react/authStore";
import { FUNCTION_MODULE } from "@zhihaoscm/meta";
import type { FunctionEntryDistributionVo, FunctionModuleTreeDTO, FunctionOperationGlobalVo } from "@zhihaoscm/entity";
import { formatNumberWithUnitAndChinese, formartTimeWithUnitAndChinese } from "@zhihaoscm/utils";
import { formatTimeEn } from "@/utils/formatter";
import { AdminTableEmpty } from "@/views/components/TableEmpty";
import { FunctionalOperationEntryChart, FunctionalOperationTrendChart } from "./FunctionalOperationCharts";
import {
    FunctionalVisitCountDialog,
    FunctionalVisitorCountDialog,
    type FunctionalDetailExport
} from "./FunctionalOperationDetailDialog";
import {
    FunctionalOperationUserVisitVolumeDetail,
    loadFunctionalUserVisitColumnConfig,
    saveFunctionalUserVisitColumnConfig,
    type FunctionalOperationUserVisitVolumeDetailProps
} from "./FunctionalOperationUserVisitVolumeDetail";

export interface FunctionalOperationQuery {
    scope?: number;
    beginTime?: string;
    endTime?: string;
}

/** 沿用旧 Vue date-select 的展示顺序，避免按数值或对象键顺序错排快捷范围。 */
const FUNCTIONAL_SCOPE_OPTIONS = [
    { value: 1, label: "全部" },
    { value: 6, label: "今日" },
    { value: 5, label: "近一周" },
    { value: 2, label: "近一月" },
    { value: 3, label: "近一季度" },
    { value: 4, label: "近一年" }
];

/** 沿用旧 Vue disabled-date 规则：禁止选择今天之后的日期，但允许选择今天。 */
export function isFunctionalOperationDateDisabled(date: Date, today = new Date()): boolean {
    return dayjs(date).isAfter(dayjs(today), "day");
}

/** 沿用旧 Vue 核心统计卡片的中文单位格式，仅展示缩写值并保留单位来源。 */
export function formatFunctionalCoreStatCount(value?: number) {
    return formatNumberWithUnitAndChinese(Number.isFinite(value) ? Number(value) : 0);
}

/** 沿用旧 Vue 停留时长卡片的中文单位展示，并保留不足一秒的特殊提示。 */
export function formatFunctionalCoreStatDuration(value?: number) {
    const normalizedValue = Number.isFinite(value) ? Number(value) : 0;
    const toDisplayTokens = (tokens: Array<{ value?: number; label?: string }>) => (
        <span className="functional-operation-stat-value">
            {tokens.flatMap((token, index) => [
                token.value !== undefined ? <span key={`${index}-value`}>{token.value}</span> : null,
                token.label ? (
                    <span key={`${index}-label`} className="functional-operation-stat-unit">
                        {token.label}
                    </span>
                ) : null
            ])}
        </span>
    );

    if (normalizedValue > 0 && normalizedValue < 1) {
        // 保持现有业务约定：不足一秒展示为“<秒”，并将单位交给 Statistic 的 suffix 渲染。
        return { value: "<", suffix: "秒", tooltip: undefined };
    }
    const formatted = formartTimeWithUnitAndChinese(normalizedValue);
    return {
        value: formatted.impreciseTokens.length ? toDisplayTokens(formatted.impreciseTokens) : 0,
        suffix: "",
        tooltip: formatted.precise
    };
}

interface FunctionalCoreStatCardProps {
    title: string;
    value: ReactNode;
    suffix?: ReactNode;
    tooltip?: string;
    color: string;
    titleBackgroundColor: string;
}

/** 对齐旧 Vue DataCard 的尺寸、标题背景和数值层级，同时保留 AntD Statistic 语义结构。 */
function FunctionalCoreStatCard({
    title,
    value,
    suffix,
    tooltip,
    color,
    titleBackgroundColor
}: FunctionalCoreStatCardProps) {
    const style = {
        "--functional-stat-color": color,
        "--functional-stat-title-background": titleBackgroundColor
    } as CSSProperties;

    return (
        <div
            data-testid="functional-operation-core-stat-card"
            className="relative flex h-[100px] min-w-[200px] justify-center rounded-lg border border-[#ebebeb]"
            style={style}
        >
            <Tooltip title={tooltip}>
                <Statistic
                    title={title}
                    // Ant Statistic 会把 ReactNode 直接转成字符串，使用 formatter 保留数字和单位的独立样式。
                    value=""
                    formatter={() => value}
                    suffix={suffix}
                    className="[&_.ant-statistic-title]:absolute [&_.ant-statistic-title]:left-0 [&_.ant-statistic-title]:top-0 [&_.ant-statistic-title]:z-[1] [&_.ant-statistic-title]:m-0 [&_.ant-statistic-title]:rounded-[8px_0] [&_.ant-statistic-title]:bg-[var(--functional-stat-title-background)] [&_.ant-statistic-title]:px-4 [&_.ant-statistic-title]:py-[7px] [&_.ant-statistic-title]:text-sm [&_.ant-statistic-title]:font-medium [&_.ant-statistic-title]:leading-[14px] [&_.ant-statistic-title]:text-[var(--functional-stat-color)] [&_.ant-statistic-content]:mt-[45px] [&_.ant-statistic-content]:flex [&_.ant-statistic-content]:items-end [&_.ant-statistic-content]:text-[var(--functional-stat-color)] [&_.ant-statistic-content]:text-[30px] [&_.ant-statistic-content]:font-semibold [&_.ant-statistic-content]:leading-[30px] [&_.ant-statistic-content-suffix]:text-base [&_.ant-statistic-content-suffix]:font-medium [&_.ant-statistic-content-suffix]:leading-4 [&_.ant-statistic-content-suffix]:text-[#bfbfbf]"
                />
            </Tooltip>
        </div>
    );
}

export interface FunctionalOperationAnalysisPageProps {
    loadStatisticByFunction?: (
        params: FunctionalOperationQuery & { functionType: number }
    ) => Promise<FunctionEntryDistributionVo | null | undefined>;
    loadStatisticAll?: (params: FunctionalOperationQuery) => Promise<FunctionOperationGlobalVo>;
    loadFunctionModules?: (params: FunctionalOperationQuery) => Promise<FunctionModuleTreeDTO[]>;
    loadVisitCountDetail?: (params: {
        moduleName: string;
        page: number;
        size: number;
        scope?: number;
        beginTime?: string;
        endTime?: string;
    }) => Promise<import("@zhihaoscm/entity").VisitorCountDetailVo[]>;
    exportVisitCountDetail?: FunctionalDetailExport;
    exportVisitorCountDetail?: FunctionalDetailExport;
    loadVisitorCountDetail?: (params: {
        moduleName: string;
        page: number;
        size: number;
        scope?: number;
        beginTime?: string;
        endTime?: string;
    }) => Promise<import("@zhihaoscm/entity").VisitCountDetailVo[]>;
    exportFunction?: (params: FunctionalOperationQuery, filename: string) => Promise<unknown>;
    loadUserVisitVolumeDetail?: FunctionalOperationUserVisitVolumeDetailProps["loadRows"];
    loadUserVisitColumnConfig?: FunctionalOperationUserVisitVolumeDetailProps["loadColumnConfig"];
    saveUserVisitColumnConfig?: FunctionalOperationUserVisitVolumeDetailProps["saveColumnConfig"];
    permissions?: string[];
}

/** 对齐旧 Vue 入口图表，按访问次数从低到高展示入口分布。 */
export function sortFunctionalEntryItems(items: FunctionEntryDistributionVo["entryItems"] = []) {
    return [...items].sort((left, right) => (left.visitCount || 0) - (right.visitCount || 0));
}

/** 兼容统计接口在无结果时返回 null/空体的情况，避免下拉切换后读取字段抛错。 */
export function normalizeFunctionalEntryItems(
    result?: Pick<FunctionEntryDistributionVo, "entryItems"> | null
): FunctionEntryDistributionVo["entryItems"] {
    return Array.isArray(result?.entryItems) ? result.entryItems : [];
}

/** 沿用 Vue 用户行为页的默认近一月时间范围和请求字段。 */
export function buildFunctionalOperationQuery(
    now = dayjs(),
    scope?: number,
    customRange: [string, string] = ["", ""]
): FunctionalOperationQuery {
    // 未传范围时保持旧页面默认近一月；只有存在自定义日期时才将快捷 scope 清除。
    const activeScope = scope ?? (customRange[0] || customRange[1] ? undefined : 2);
    if (activeScope === undefined) {
        return {
            scope: undefined,
            ...(customRange[0] ? { beginTime: `${customRange[0]} 00:00:00` } : {}),
            ...(customRange[1] ? { endTime: `${customRange[1]} 23:59:59` } : {})
        };
    }
    // 旧 Vue 仅将“今日”scope=6 转为 undefined，“全部”仍提交 scope=1。
    if (activeScope === 1) return { scope: 1 };

    const end = now.endOf("day");
    const start =
        activeScope === 6
            ? end.startOf("day")
            : activeScope === 5
              ? end.subtract(1, "week").add(1, "day").startOf("day")
              : activeScope === 3
                ? end.subtract(3, "month").add(1, "day").startOf("day")
                : activeScope === 4
                  ? end.subtract(1, "year").add(1, "day").startOf("day")
                  : end.subtract(1, "month").add(1, "day").startOf("day");

    return {
        scope: activeScope === 6 ? undefined : activeScope,
        beginTime: start.format("YYYY-MM-DDTHH:mm:ss"),
        endTime: end.format("YYYY-MM-DDTHH:mm:ss")
    };
}

/** 沿用旧 Vue data-wrap 的日期文案，仅展示查询参数对应的日期部分。 */
export function formatFunctionalOperationDateRange(query: FunctionalOperationQuery, now = dayjs()): string {
    if (query.scope === 1 && !query.beginTime && !query.endTime) return "";
    const effectiveQuery = query.beginTime && query.endTime ? query : buildFunctionalOperationQuery(now, query.scope);
    if (!effectiveQuery.beginTime || !effectiveQuery.endTime) return "";

    const begin = dayjs(effectiveQuery.beginTime);
    const end = dayjs(effectiveQuery.endTime);
    if (!begin.isValid() || !end.isValid()) return "";
    return `${begin.format("YYYY-MM-DD")} ~ ${end.format("YYYY-MM-DD")}`;
}

/** 复用旧 Vue dashboardService 的功能入口分布接口。 */
const loadStatisticByFunction = (params: FunctionalOperationQuery & { functionType: number }) =>
    dashboardService.getStatisticByFunction(params);

/** 复用旧 Vue dashboardService 的顶部统计和趋势接口。 */
const loadStatisticAll = (params: FunctionalOperationQuery) => dashboardService.getStatisticAll(params);

/** 复用旧 Vue dashboardService 的功能模块树接口。 */
const loadFunctionModules = (params: FunctionalOperationQuery) => dashboardService.functionQueryAll(params);

/** 复用旧 Vue 功能模块导出接口，保持下载参数和文件名契约。 */
const exportFunction = (params: FunctionalOperationQuery, filename: string) =>
    dashboardService.exportFunction(params, filename);

const renderModuleRows = (
    rows: FunctionModuleTreeDTO[],
    onVisitCount: (row: FunctionModuleTreeDTO) => void,
    onVisitorCount: (row: FunctionModuleTreeDTO) => void,
    expandedKeys: Set<string>,
    onToggleExpanded: (key: string) => void,
    parentKey = "root",
    level = 0
): ReactNode[] =>
    rows.flatMap((row, index) => {
        const rowKey = `${parentKey}-${index}-${row.moduleName}`;
        const hasChildren = Boolean(row.children?.length);
        const isExpanded = expandedKeys.has(rowKey);
        const displayIndex = (row as FunctionModuleTreeDTO & { displayIndex?: number }).displayIndex;
        return [
            <tr key={rowKey}>
                <td>{level === 0 ? (displayIndex ?? index + 1) : ""}</td>
                <td style={{ paddingLeft: `${level * 20 + 12}px` }}>
                    <div className="flex items-center">
                        {hasChildren ? (
                            <button
                                type="button"
                                aria-label={`${isExpanded ? "收起" : "展开"}${row.moduleName}`}
                                className={`mr-2 flex h-4 w-2.5 items-center justify-center border-0 bg-transparent p-0 text-[#bfbfbf] ${
                                    isExpanded ? "rotate-90" : ""
                                }`}
                                onClick={() => onToggleExpanded(rowKey)}
                            >
                                <span aria-hidden="true" className="iconfont icon-arrow-rifht text-[10px]" />
                            </button>
                        ) : (
                            <span aria-hidden="true" className="mr-2 inline-block h-4 w-2.5" />
                        )}
                        <span className={hasChildren ? "font-['PingFang_SC'] text-[#000000e0]" : ""}>
                            {row.moduleName}
                        </span>
                    </div>
                </td>
                <td>{formatTimeEn(Number(row.avgUserStayTime))}</td>
                <td>{formatTimeEn(Number(row.avgSingleStayTime))}</td>
                <td>
                    {row.children?.length && row.visitCount ? (
                        <button
                            type="button"
                            className="text-blue-600 hover:underline"
                            aria-label={`查看${row.moduleName}访问次数明细`}
                            onClick={() => onVisitCount(row)}
                        >
                            {row.visitCount}
                        </button>
                    ) : (
                        (row.visitCount ?? "--")
                    )}
                </td>
                <td>
                    {row.children?.length && row.visitorCount ? (
                        <button
                            type="button"
                            className="text-blue-600 hover:underline"
                            aria-label={`查看${row.moduleName}访问人数明细`}
                            onClick={() => onVisitorCount(row)}
                        >
                            {row.visitorCount}
                        </button>
                    ) : (
                        (row.visitorCount ?? "--")
                    )}
                </td>
                <td>{row.entryCount}</td>
            </tr>,
            ...(hasChildren && isExpanded
                ? renderModuleRows(
                      row.children || [],
                      onVisitCount,
                      onVisitorCount,
                      expandedKeys,
                      onToggleExpanded,
                      rowKey,
                      level + 1
                  )
                : [])
        ];
    });

type ModuleSortKey = "avgUserStayTime" | "avgSingleStayTime" | "visitCount" | "visitorCount";
type ModuleSortOrder = "asc" | "desc";

/** 对齐旧 Vue 首层功能模块排序：只重排首层，子模块继续跟随父模块展示。 */
export function sortFunctionModuleTreeFirstLevel(
    rows: FunctionModuleTreeDTO[],
    sortKey: ModuleSortKey,
    sortOrder: ModuleSortOrder
): FunctionModuleTreeDTO[] {
    return [...rows].sort((left, right) => {
        const leftValue = left[sortKey] as number | string | null | undefined;
        const rightValue = right[sortKey] as number | string | null | undefined;
        if (leftValue == null && rightValue == null) return 0;
        if (leftValue == null) return sortOrder === "asc" ? 1 : -1;
        if (rightValue == null) return sortOrder === "asc" ? -1 : 1;
        if (typeof leftValue === "number" && typeof rightValue === "number") {
            return sortOrder === "asc" ? leftValue - rightValue : rightValue - leftValue;
        }
        const result = String(leftValue).localeCompare(String(rightValue));
        return sortOrder === "asc" ? result : -result;
    });
}

/** React 版功能运营分析，保留旧 Vue 路由对应的数据接口和核心字段。 */
export function FunctionalOperationAnalysisPage({
    loadStatisticByFunction: loadStatisticByFunctionProp = loadStatisticByFunction,
    loadStatisticAll: loadStatisticAllProp = loadStatisticAll,
    loadFunctionModules: loadFunctionModulesProp = loadFunctionModules,
    loadVisitCountDetail,
    loadVisitorCountDetail,
    exportVisitCountDetail,
    exportVisitorCountDetail,
    exportFunction: exportFunctionProp = exportFunction,
    loadUserVisitVolumeDetail,
    loadUserVisitColumnConfig = loadFunctionalUserVisitColumnConfig,
    saveUserVisitColumnConfig = saveFunctionalUserVisitColumnConfig,
    permissions
}: FunctionalOperationAnalysisPageProps = {}) {
    const [statistic, setStatistic] = useState<FunctionOperationGlobalVo>();
    const [entryDistribution, setEntryDistribution] = useState<FunctionEntryDistributionVo["entryItems"]>([]);
    const [moduleRows, setModuleRows] = useState<FunctionModuleTreeDTO[]>([]);
    const [loading, setLoading] = useState(true);
    const [entryLoading, setEntryLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [moduleSort, setModuleSort] = useState<{ key: ModuleSortKey; order: ModuleSortOrder }>();
    const [detail, setDetail] = useState<{ type: "visit" | "visitor"; row: FunctionModuleTreeDTO }>();
    const [exporting, setExporting] = useState(false);
    const [expandedModuleKeys, setExpandedModuleKeys] = useState<Set<string>>(() => new Set());
    const storePermissions = useUserStore(state => state.userInfo.permissions || []);
    // 对齐旧 Vue v-auth：核心功能导出入口仅向具备独立导出权限的用户展示。
    const canExport = (permissions ?? storePermissions).includes("ROLE_SAND_FUNCTION_BI_EXPORT");
    const [scope, setScope] = useState<number | undefined>(2);
    const [customRange, setCustomRange] = useState<[string, string]>(["", ""]);
    const [functionType, setFunctionType] = useState(1);
    const pageRef = useRef<HTMLElement>(null);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const currentQuery = useMemo(
        () => buildFunctionalOperationQuery(dayjs(), scope, customRange),
        [customRange, scope]
    );

    const selectScope = (nextScope: number) => {
        // 快捷范围接管查询条件时清空自定义日期，保持旧 Vue 选择器的回显状态一致。
        // zhihao DateRangePicker 在受控值同步时可能重复触发 change；值未变化时禁止再次写入，避免更新深度递归。
        setScope(current => (current === nextScope ? current : nextScope));
        setCustomRange(current => (current[0] || current[1] ? ["", ""] : current));
    };

    const selectCustomRange = (_value: unknown, formattedValue: [string, string] | null) => {
        const [start = "", end = ""] = formattedValue || [];
        // 受控日期组件回显同一范围时不应重复触发父组件重渲染。
        setScope(current => (current === undefined ? current : undefined));
        setCustomRange(current => (current[0] === start && current[1] === end ? current : [start, end]));
    };

    useEffect(() => {
        const syncFullscreenState = () => setIsFullscreen(document.fullscreenElement === pageRef.current);
        document.addEventListener("fullscreenchange", syncFullscreenState);
        return () => document.removeEventListener("fullscreenchange", syncFullscreenState);
    }, []);

    /** 复用 Vue useFullscreen 行为，在浏览器不支持全屏 API 时安全忽略。 */
    const toggleFullscreen = () => {
        if (document.fullscreenElement) {
            void document.exitFullscreen?.();
            return;
        }
        void pageRef.current?.requestFullscreen?.();
    };

    useEffect(() => {
        let disposed = false;

        setLoading(true);
        void Promise.all([loadStatisticAllProp(currentQuery), loadFunctionModulesProp(currentQuery)])
            .then(([statisticResult, moduleResult]) => {
                if (disposed) return;
                setStatistic(statisticResult);
                // 首层编号随接口返回结果固定，排序时保留旧 Vue 的原始编号。
                setModuleRows((moduleResult || []).map((row, index) => ({ ...row, displayIndex: index + 1 })));
                setErrorMessage(null);
            })
            .catch(() => {
                if (!disposed) setErrorMessage("用户行为数据加载失败");
            })
            .finally(() => {
                if (!disposed) setLoading(false);
            });

        return () => {
            disposed = true;
        };
    }, [currentQuery, loadFunctionModulesProp, loadStatisticAllProp]);

    // 对齐旧 Vue 功能模块切换行为：仅刷新入口访问量，不重复请求其他统计数据。
    useEffect(() => {
        let disposed = false;

        setEntryLoading(true);
        void loadStatisticByFunctionProp({ ...currentQuery, functionType })
            .then(entryResult => {
                if (disposed) return;
                // 下拉查询无数据时仍完成当前请求，把空结果交给图表而不是进入异常分支。
                setEntryDistribution(sortFunctionalEntryItems(normalizeFunctionalEntryItems(entryResult)));
                setErrorMessage(null);
            })
            .catch(() => {
                if (!disposed) setErrorMessage("用户行为数据加载失败");
            })
            .finally(() => {
                if (!disposed) setEntryLoading(false);
            });

        return () => {
            disposed = true;
        };
    }, [currentQuery, functionType, loadStatisticByFunctionProp]);

    const topStat = statistic?.topStat;
    const visitorCount = formatFunctionalCoreStatCount(topStat?.visitorCount);
    const visitCount = formatFunctionalCoreStatCount(topStat?.visitCount);
    const avgUserStayTime = formatFunctionalCoreStatDuration(topStat?.avgUserStayTime);
    const avgSingleStayTime = formatFunctionalCoreStatDuration(topStat?.avgSingleStayTime);
    const dateRangeLabel = formatFunctionalOperationDateRange(currentQuery);
    // Vue 会在快捷范围切换后同步日期框回显，避免筛选状态和实际请求区间不一致。
    // DateRangePicker 在 layout effect 中会同步受控值；保持数组引用稳定，避免每次渲染触发重复 setState。
    const displayedDateRange = useMemo<[string, string] | null>(
        () =>
            currentQuery.beginTime && currentQuery.endTime
                ? [currentQuery.beginTime.slice(0, 10), currentQuery.endTime.slice(0, 10)]
                : null,
        [currentQuery.beginTime, currentQuery.endTime]
    );
    /** 沿用旧 Vue data-wrap 标题：蓝色竖线、标题文案和日期范围分层展示。 */
    const panelTitle = (title: string, withDate = true) => (
        <span data-testid="functional-operation-panel-title" className="flex items-center">
            <span
                data-testid="functional-operation-panel-title-mark"
                aria-hidden="true"
                className="mr-1.5 inline-block h-3.5 w-0.5 shrink-0 rounded bg-[#3370ff]"
            />
            <span
                className="functional-operation-panel-title-text text-sm font-medium leading-[14px] text-[#000000e0]"
                style={{ fontSize: 14, fontWeight: 500, lineHeight: "14px" }}
            >
                {title}
            </span>
            {withDate && dateRangeLabel ? (
                <span className="ml-2 text-xs font-normal leading-3 text-black/40">{dateRangeLabel}</span>
            ) : null}
        </span>
    );
    const trendCount = statistic?.trendChart?.userCountTrend?.length || 0;
    const sortedModuleRows = useMemo(
        () =>
            moduleSort ? sortFunctionModuleTreeFirstLevel(moduleRows, moduleSort.key, moduleSort.order) : moduleRows,
        [moduleRows, moduleSort]
    );

    // 复用旧 ProTable 的三态排序交互：升序、降序、清除排序后恢复接口原始顺序。
    const toggleModuleSort = (key: ModuleSortKey) => {
        setModuleSort(current => {
            if (!current || current.key !== key) return { key, order: "asc" };
            if (current.order === "asc") return { key, order: "desc" };
            return undefined;
        });
    };

    /** 对齐旧 Vue ProTable 树表默认收起行为，单独切换父模块不影响其他模块。 */
    const toggleModuleExpanded = (key: string) => {
        setExpandedModuleKeys(current => {
            const next = new Set(current);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };

    // 对齐旧 Vue 表头展开按钮：只控制当前首层拥有子模块的节点。
    const expandableModuleKeys = sortedModuleRows.flatMap((row, index) =>
        row.children?.length ? [`root-${index}-${row.moduleName}`] : []
    );
    const areAllModulesExpanded =
        expandableModuleKeys.length > 0 && expandableModuleKeys.every(key => expandedModuleKeys.has(key));
    const toggleAllModulesExpanded = () => {
        setExpandedModuleKeys(current => {
            if (areAllModulesExpanded) return new Set();
            return new Set([...current, ...expandableModuleKeys]);
        });
    };

    const exportCurrent = async () => {
        setExporting(true);
        try {
            await exportFunctionProp(currentQuery, `核心功能运营分析${dayjs().format("YYYYMMDD")}`);
            message.success("导出成功");
        } catch {
            setErrorMessage("核心功能运营分析导出失败");
        } finally {
            setExporting(false);
        }
    };

    return (
        <main
            ref={pageRef}
            className={`functional-operation-analysis-page flex h-[calc(var(--zh-layout-main-height))] min-h-0 w-full flex-col gap-2 overflow-hidden bg-[#f7f8fb] pb-2 pl-0 pr-2 pt-0 ${
                isFullscreen ? "fixed inset-0 z-[1000] bg-[#edf3fe] p-2" : ""
            }`}
            aria-label="用户行为"
        >
            <header className="functional-operation-analysis-header relative rounded-lg bg-white p-5">
                <h1
                    className="m-0 mb-[16px] text-lg font-medium leading-[18px] text-[#000000e0]"
                    style={{ fontSize: 18, fontWeight: 500, lineHeight: "18px" }}
                >
                    用户行为
                </h1>
                <Button
                    type="text"
                    className="functional-operation-fullscreen-button absolute right-5 top-5 z-[999] h-8 w-8 rounded-[5px] bg-[#3370ff1a] p-0 text-[#3370ff]"
                    aria-label={isFullscreen ? "退出全屏" : "进入全屏"}
                    onClick={toggleFullscreen}
                >
                    <span
                        aria-hidden="true"
                        className={`iconfont text-base ${isFullscreen ? "icon-quxiaoquanping" : "icon-quanping1"}`}
                    />
                </Button>
                <div className="admin-filter-form functional-operation-filter-form">
                    <div
                        role="radiogroup"
                        aria-label="统计范围"
                        className="functional-operation-scope-select h-[34px] rounded-[5px] bg-[#f5f5f5] p-0.5"
                    >
                        {FUNCTIONAL_SCOPE_OPTIONS.map(({ value, label }) => (
                            <label
                                key={value}
                                role="radio"
                                aria-checked={scope === value}
                                className={`functional-operation-scope-option h-[30px] cursor-pointer rounded-[5px] px-2 text-sm font-normal text-[#333] ${
                                    scope === value ? "is-active font-medium" : ""
                                }`}
                                onClick={() => selectScope(value)}
                            >
                                {label}
                            </label>
                        ))}
                    </div>
                    <ZhDateRangePicker
                        aria-label="自定义用户行为日期"
                        className="w-40"
                        rootClassName="functional-operation-date-picker [&.zh-datetime-picker]:rounded-[6px] [&.zh-datetime-picker]:bg-[#f5f5f5] [&.zh-datetime-picker]:shadow-none [&.zh-datetime-picker:hover]:shadow-[inset_0_0_0_1px_#3370ff] [&.zh-datetime-picker:focus-within]:shadow-[inset_0_0_0_1px_#3370ff] [&_.ant-picker-input>input]:text-xs [&_.ant-picker-range-separator]:text-xs [&_.ant-picker-range-separator]:text-[#00000066]"
                        type="daterange"
                        placement="bottom-end"
                        rangeSeparator="-"
                        value={displayedDateRange}
                        clearable={false}
                        valueFormat="YYYY-MM-DD"
                        format="YYYY-MM-DD"
                        startPlaceholder="开始日期"
                        endPlaceholder="结束日期"
                        disabledDate={isFunctionalOperationDateDisabled}
                        onChange={selectCustomRange}
                    />
                </div>
            </header>
            <div data-testid="functional-operation-content" className="overflow-x-hidden overflow-y-auto">
                <div
                    data-testid="functional-operation-data-panel-layout"
                    className="flex w-full flex-wrap items-start justify-between gap-2"
                >
                    <Card
                        data-testid="functional-operation-core-panel"
                        className="functional-operation-analysis-card w-full min-w-[992px] min-[1920px]:w-[56%] min-[1920px]:flex-none [&_.ant-card-body]:p-5 [&_.ant-card-body]:pb-2.5 [&_.ant-card-head]:px-5"
                        aria-busy={loading}
                    >
                        <div className="functional-operation-panel-header flex h-8 items-center">
                            {panelTitle("核心功能访问量统计")}
                        </div>
                        {errorMessage ? (
                            <Alert className="mt-4" message={errorMessage} role="alert" type="error" />
                        ) : null}
                        <section aria-label="功能运营分析统计" className="mt-4 grid w-full grid-cols-4 gap-x-5 gap-y-4">
                            <FunctionalCoreStatCard
                                title={`访问人数${visitorCount.baseUnit ? `(${visitorCount.baseUnit})` : ""}`}
                                value={visitorCount.imprecise}
                                tooltip={visitorCount.precise}
                                color="#0997FF"
                                titleBackgroundColor="#0997ff1a"
                            />
                            <FunctionalCoreStatCard
                                title={`访问次数${visitCount.baseUnit ? `(${visitCount.baseUnit})` : ""}`}
                                value={visitCount.imprecise}
                                tooltip={visitCount.precise}
                                color="#0997FF"
                                titleBackgroundColor="#0997ff1a"
                            />
                            <FunctionalCoreStatCard
                                title="平均用户总停留时长"
                                value={avgUserStayTime.value}
                                suffix={avgUserStayTime.suffix}
                                tooltip={avgUserStayTime.tooltip}
                                color="#3370ff"
                                titleBackgroundColor="#f3f8ff"
                            />
                            <FunctionalCoreStatCard
                                title="平均单次停留时长"
                                value={avgSingleStayTime.value}
                                suffix={avgSingleStayTime.suffix}
                                tooltip={avgSingleStayTime.tooltip}
                                color="#3370ff"
                                titleBackgroundColor="#f3f8ff"
                            />
                        </section>
                        <div className="mt-0" data-trend-count={trendCount}>
                            <FunctionalOperationTrendChart
                                data={statistic?.trendChart?.userCountTrend}
                                onFunctionTypeChange={setFunctionType}
                            />
                        </div>
                    </Card>

                    <Card
                        data-testid="functional-operation-entry-panel"
                        className="functional-operation-analysis-card functional-operation-entry-panel-card min-w-[744px] flex-1 [&_.ant-card-body]:p-5 [&_.ant-card-head]:px-5"
                        aria-busy={loading || entryLoading}
                    >
                        <div className="functional-operation-panel-header flex h-8 items-center justify-between">
                            {panelTitle("入口访问量统计")}
                            <Select
                                aria-label="功能模块"
                                style={{ width: 150 }}
                                className="functional-operation-entry-select text-xs [&_.ant-select-selector]:rounded-[6px] [&_.ant-select-selector]:bg-[#f5f5f5] [&_.ant-select-selector]:shadow-none [&:hover_.ant-select-selector]:shadow-[0_0_0_1px_#3370ff_inset] [&.ant-select-focused_.ant-select-selector]:shadow-[0_0_0_1px_#3370ff_inset]"
                                value={functionType}
                                options={FUNCTION_MODULE.options.map(item => ({
                                    label: item.label,
                                    value: Number(item.value)
                                }))}
                                onChange={value => setFunctionType(Number(value))}
                            />
                        </div>
                        <FunctionalOperationEntryChart data={entryDistribution} />
                        <ul aria-label="入口访问量列表" className="sr-only">
                            {entryDistribution.map(item => (
                                <li key={item.entryName}>
                                    <span>{item.entryName}</span>
                                    <span>{item.visitCount}</span>
                                </li>
                            ))}
                        </ul>
                    </Card>
                </div>

                <Card
                    className="functional-operation-module-card functional-operation-analysis-card border-0 [&_.ant-card-body]:p-5 [&_.ant-card-head]:px-5"
                    aria-busy={loading}
                >
                    <div className="functional-operation-panel-header flex h-[14px] items-center">
                        {panelTitle("核心功能访问量明细", false)}
                    </div>
                    <div className="mt-[16px] mb-[14px]">
                        {canExport ? (
                            <Button
                                type="primary"
                                loading={exporting}
                                aria-label="导出核心功能访问量明细"
                                onClick={() => void exportCurrent()}
                            >
                                导出
                            </Button>
                        ) : null}
                    </div>
                    <table aria-label="功能模块统计" className="functional-operation-module-table w-full text-left">
                        <thead>
                            <tr>
                                <th style={{ minWidth: 180 }}>编号</th>
                                <th style={{ minWidth: 350 }}>
                                    <button
                                        type="button"
                                        aria-label={`${areAllModulesExpanded ? "收起" : "展开"}全部功能模块`}
                                        title={`${areAllModulesExpanded ? "收起" : "展开"}全部功能模块`}
                                        className="mr-1 flex h-3.5 w-3.5 flex-col cursor-pointer border-0 bg-transparent p-0"
                                        onClick={toggleAllModulesExpanded}
                                    >
                                        <span
                                            aria-hidden="true"
                                            className={`iconfont icon-up h-3.5 w-3.5 text-sm leading-[14px] text-[#bfbfbf] ${
                                                areAllModulesExpanded
                                                    ? "translate-y-[5px] rotate-180"
                                                    : "-translate-y-0.5"
                                            }`}
                                        />
                                        <span
                                            aria-hidden="true"
                                            className={`iconfont icon-down h-3.5 w-3.5 text-sm leading-[14px] text-[#bfbfbf] ${
                                                areAllModulesExpanded ? "-translate-y-px rotate-180" : "-translate-y-2"
                                            }`}
                                        />
                                    </button>
                                    <span>功能名称</span>
                                </th>
                                <th style={{ minWidth: 250 }}>
                                    <button type="button" onClick={() => toggleModuleSort("avgUserStayTime")}>
                                        平均用户停留时长
                                    </button>
                                </th>
                                <th style={{ minWidth: 250 }}>
                                    <button type="button" onClick={() => toggleModuleSort("avgSingleStayTime")}>
                                        平均单次停留时长
                                    </button>
                                </th>
                                <th style={{ minWidth: 180 }}>
                                    <button type="button" onClick={() => toggleModuleSort("visitCount")}>
                                        访问次数
                                    </button>
                                </th>
                                <th style={{ minWidth: 180 }}>
                                    <button type="button" onClick={() => toggleModuleSort("visitorCount")}>
                                        访问人数
                                    </button>
                                </th>
                                <th style={{ minWidth: 180 }}>功能入口数</th>
                            </tr>
                        </thead>
                        <tbody>
                            {sortedModuleRows.length ? (
                                renderModuleRows(
                                    sortedModuleRows,
                                    row => setDetail({ type: "visit", row }),
                                    row => setDetail({ type: "visitor", row }),
                                    expandedModuleKeys,
                                    toggleModuleExpanded
                                )
                            ) : (
                                <tr>
                                    <td colSpan={7} className="py-8 text-center text-black/40">
                                        <AdminTableEmpty />
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </Card>
                <FunctionalOperationUserVisitVolumeDetail
                    searchParams={currentQuery}
                    loadRows={loadUserVisitVolumeDetail}
                    loadColumnConfig={loadUserVisitColumnConfig}
                    saveColumnConfig={saveUserVisitColumnConfig}
                />
            </div>
            {detail?.type === "visit" ? (
                <FunctionalVisitCountDialog
                    open
                    info={detail.row}
                    searchParams={currentQuery}
                    loadRows={loadVisitCountDetail}
                    exportRows={exportVisitCountDetail}
                    canExport={canExport}
                    onClose={() => setDetail(undefined)}
                />
            ) : null}
            {detail?.type === "visitor" ? (
                <FunctionalVisitorCountDialog
                    open
                    info={detail.row}
                    searchParams={currentQuery}
                    loadRows={loadVisitorCountDetail}
                    exportRows={exportVisitorCountDetail}
                    canExport={canExport}
                    onClose={() => setDetail(undefined)}
                />
            ) : null}
        </main>
    );
}

export default FunctionalOperationAnalysisPage;
